import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";

import tzlookup from "tz-lookup";
import { BACKEND_DATE_FORMAT } from "../../../consts";
import { getFilePreSignedUrl } from "../../../utils/api";
import { addOpenMeteoForecastRequests } from "../../../utils/openMeteoForecast";
import { blobDate, blobSitePrefix } from "../../../utils/demoSiteBlob";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

const VISUAL_CROSSING_API_KEY = "3VKSWKB5Q6Q3AAFW6CKKPBX29";
const WEATHER_BIT_API_KEY = "41b80ceae38b492a8976849b65387e19";
const DATE_FORMAT = "YYYY-MM-DD";
const GRAPH_UI_DATE_FORMAT = "DD MMM YY";
const weatherApiStatus = {
  visualCrossingExpired: false,
  weatherBitExpired: false,
  openMeteoExpired: false,
};

const fetchFromVisualCrossing = async (latLong, startDate, endDate) => {
  if (!latLong) {
    return;
  }
  const apiUrl = `https://weather.visualcrossing.com/VisualCrossingWebServices/rest/services/timeline/${latLong}/${startDate}/${endDate}?unitGroup=metric&include=days&key=${VISUAL_CROSSING_API_KEY}&contentType=json`;

  try {
    const response = await fetch(apiUrl);
    if (!response.ok) {
      if (response.status === 429) {
        console.error("Visual Crossing API limit reached.");
        weatherApiStatus.visualCrossingExpired = true;
      }
      return null;
    }

    const data = await response.json();
    return data.days.map((day) => ({
      date: dayjs(day.datetime).format(DATE_FORMAT),
      max: day.tempmax,
      min: day.tempmin,
    }));
  } catch (error) {
    console.error("Error fetching from Visual Crossing:", error);
    weatherApiStatus.visualCrossingExpired = true;
    return null;
  }
};

const fetchFromWeatherBit = async (latLong, startDate, endDate) => {
  const [lat, lon] = latLong.split(",");
  const apiUrl = `https://api.weatherbit.io/v2.0/history/daily?lat=${lat.trim()}&lon=${lon.trim()}&start_date=${startDate}&end_date=${endDate}&key=${WEATHER_BIT_API_KEY}`;

  try {
    const response = await fetch(apiUrl);
    if (response.status === 429) {
      // API limit reached
      weatherApiStatus.weatherbitExpired = true;
      return null;
    }

    const data = await response.json();
    return data.data.map((day) => ({
      date: day.datetime,
      max: day.max_temp,
      min: day.min_temp,
    }));
  } catch (error) {
    console.error("Error fetching from Weatherbit:", error);
    weatherApiStatus.weatherbitExpired = true;
    return null;
  }
};

// Function to fetch from Open-Meteo API (no key required)
const fetchFromOpenMeteo = async (
  latLong,
  startDate,
  endDate,
  siteMetadata,
) => {
  // Initialize array to hold all temperature data
  const allTemperatureData = [];

  try {
    // 1. **Validate and Parse Inputs**

    // Validate and parse latLong
    const [latStr, lonStr] = latLong.split(",").map((coord) => coord.trim());
    const lat = parseFloat(latStr);
    const lon = parseFloat(lonStr);
    if (
      isNaN(lat) ||
      isNaN(lon) ||
      lat < -90 ||
      lat > 90 ||
      lon < -180 ||
      lon > 180
    ) {
      throw new Error(
        'Invalid latLong format. Expected format "lat,lon" with valid numerical values.',
      );
    }

    // Parse and validate input dates
    const inputStartDate = dayjs(startDate, DATE_FORMAT, true);
    const inputEndDate = dayjs(endDate, DATE_FORMAT, true);
    if (!inputStartDate.isValid() || !inputEndDate.isValid()) {
      throw new Error('Invalid date format. Expected format "YYYY-MM-DD".');
    }

    if (inputEndDate.isBefore(inputStartDate)) {
      throw new Error("endDate cannot be earlier than startDate.");
    }

    // 2. **Define Date Boundaries**

    const today = getTodayDate(latLong);
    if (!today || !today.isValid()) {
      throw new Error("Failed to retrieve today's date.");
    }

    // Define valid date ranges for each API
    const historicalStartMin = dayjs("1940-01-01");
    const historicalEndMax = today.subtract(3, "day"); // today - 3 days

    const forecastStartMin = historicalEndMax.add(1, "day"); // today - 2 days
    const forecastEndMax = today.add(14, "day"); // today + 14 days

    // 3. **Clamp Input Dates to Valid Ranges**

    const clampedStartDate = inputStartDate.isBefore(historicalStartMin)
      ? historicalStartMin
      : inputStartDate;

    const clampedEndDate = inputEndDate.isAfter(forecastEndMax)
      ? forecastEndMax
      : inputEndDate;

    // 4. **Determine Required Data Fetches**

    const needsHistorical = clampedStartDate.isSameOrBefore(historicalEndMax);
    const needsForecast = clampedEndDate.isSameOrAfter(forecastStartMin);

    if (!needsHistorical && !needsForecast) {
      console.warn(
        "Requested date range does not fall within the supported API ranges.",
      );
      return allTemperatureData;
    }

    // 5. **Get Timezone**
    const timezone = getTimezone(latLong);
    if (!timezone) {
      throw new Error("Failed to retrieve timezone.");
    }

    // 6. **Prepare Fetch URLs**

    const fetchPromises = [];

    // Historical API Fetch
    if (needsHistorical) {
      const histStart = clampedStartDate.isBefore(historicalStartMin)
        ? historicalStartMin
        : clampedStartDate;
      const histEnd = clampedEndDate.isBefore(historicalEndMax)
        ? clampedEndDate
        : historicalEndMax;

      if (!histStart.isAfter(histEnd)) {
        const historicalUrl = `https://archive-api.open-meteo.com/v1/archive?latitude=${lat}&longitude=${lon}&start_date=${histStart.format(DATE_FORMAT)}&end_date=${histEnd.format(DATE_FORMAT)}&daily=temperature_2m_max,temperature_2m_min&timezone=${encodeURIComponent(timezone)}`;
        fetchPromises.push(
          fetch(historicalUrl)
            .then((response) => {
              if (!response.ok) {
                throw new Error(
                  `Historical API responded with status ${response.status}`,
                );
              }
              return response.json();
            })
            .then((data) => {
              if (!data.daily || !data.daily.time) {
                throw new Error(
                  "Invalid historical data format received from API.",
                );
              }
              return data.daily.time.map((date, index) => ({
                date: dayjs(date).format(DATE_FORMAT),
                max: data.daily.temperature_2m_max[index],
                min: data.daily.temperature_2m_min[index],
              }));
            }),
        );
      }
    }

    // Forecast API Fetch
    if (needsForecast) {
      const foreStart = clampedStartDate.isAfter(forecastStartMin)
        ? clampedStartDate
        : forecastStartMin;
      const foreEnd = clampedEndDate.isBefore(forecastEndMax)
        ? clampedEndDate
        : forecastEndMax;

      if (!foreStart.isAfter(foreEnd)) {
        addOpenMeteoForecastRequests({
          fetchPromises,
          lat,
          lon,
          forecastStart: foreStart,
          forecastEnd: foreEnd,
          timezone,
          siteMetadata,
          dateFormat: DATE_FORMAT,
        });
      }
    }

    // 7. **Execute Fetches Concurrently**

    const results = await Promise.allSettled(fetchPromises);

    results.forEach((result) => {
      if (result.status === "fulfilled") {
        allTemperatureData.push(...result.value);
      } else {
        console.error("Error fetching temperature data:", result.reason);
        // Optionally, handle specific errors or set a status flag here
      }
    });

    return allTemperatureData;
  } catch (error) {
    console.error("Unexpected error in fetchFromOpenMeteo:", error);
    // Optionally, handle or propagate the error as needed
    return null;
  }
};

export const getTimezone = (latLong) => {
  if (!latLong) return null;
  const [lat, lon] = latLong.split(",").map(Number);
  return tzlookup(lat, lon);
};

export const fetchTemperatureData = async (
  latLong,
  startDate,
  endDate,
  siteMetadata,
) => {
  if (!latLong) {
    return null;
  }
  try {
    // Try Open-Meteo if not expired
    if (!weatherApiStatus.openMeteoExpired) {
      // console.log("Trying Open Meteo...");
      const openMeteoData = await fetchFromOpenMeteo(
        latLong,
        startDate,
        endDate,
        siteMetadata,
      );
      if (Array.isArray(openMeteoData) && openMeteoData.length) {
        return openMeteoData;
      }
    }

    // If Open-Meteo failed or expired, try Visual Crossing
    if (!weatherApiStatus.visualCrossingExpired) {
      console.log("Trying Visual Crossing...");
      const visualCrossingData = await fetchFromVisualCrossing(
        latLong,
        startDate,
        endDate,
      );
      if (visualCrossingData) {
        return visualCrossingData;
      }
    }

    // If Visual Crossing failed or expired, try weatherBit
    // if (!weatherApiStatus.weatherBitExpired) {
    //   console.log("Trying weatherBit...");
    //   const weatherBitData = await fetchFromWeatherBit(startDate, endDate);
    //   if (weatherBitData) {
    //     return weatherBitData;
    //   }
    // }

    // If more APIs are added, you can continue chaining them here
    console.error("All APIs have expired or failed.");
    return null;
  } catch (error) {
    console.error("Error fetching temperature data:", error);
    return null;
  }
};

export const getTodayDate = (latLong) => {
  if (!latLong) return dayjs();
  const timeZone = getTimezone(latLong);
  return dayjs().tz(timeZone);
};

export const hexToRgba = (hex, alpha) => {
  // Remove the hash at the start if it's there
  hex = hex.replace(/^#/, "");

  // Parse the r, g, b values
  let r = parseInt(hex.substring(0, 2), 16);
  let g = parseInt(hex.substring(2, 4), 16);
  let b = parseInt(hex.substring(4, 6), 16);

  // Return the rgba color with the specified alpha
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export function evaluateSingleRule(rule, context) {
  // timeWindow check
  if (rule.timeWindow) {
    const {
      field: timeField,
      startOffset = 0,
      endOffset = 0,
      unit = "day",
      comparator,
    } = rule.timeWindow;

    const baseDate = context[timeField];
    const measureDate = context.dataCollectedDate;

    if (!baseDate?.isValid() || !measureDate?.isValid()) return false;

    const startBoundary = baseDate.clone().add(startOffset, unit);
    const endBoundary = baseDate.clone().add(endOffset, unit);

    switch (comparator) {
      case "within":
        if (!measureDate.isBetween(startBoundary, endBoundary, null, "[]")) {
          return false;
        }
        break;
      case "before":
        if (!measureDate.isBefore(startBoundary)) {
          return false;
        }
        break;
      case "after":
        if (!measureDate.isAfter(startBoundary)) {
          return false;
        }
        break;
      case "outside":
        if (
          !measureDate.isBefore(startBoundary) &&
          !measureDate.isAfter(endBoundary)
        ) {
          return false;
        }
        break;
      default:
        return false;
    }
  }

  // Evaluate the main operator
  let leftVal = null;
  switch (rule.field) {
    case "harvestDate":
      leftVal = context.harvestDate?.valueOf() || null;
      break;
    case "dataCollectedDate":
      leftVal = context.dataCollectedDate?.valueOf() || null;
      break;
    case "plantingDate":
      leftVal = context.plantingDate?.valueOf() || null;
      break;
    case "averagePercentile80_95":
      leftVal = context.averagePercentile80_95;
      break;
    case "averageLength":
      leftVal = context.averageLength;
      break;
    default:
      return false;
  }

  if (leftVal == null) {
    return false;
  }

  const rightVal = rule.value;
  switch (rule.operator) {
    case ">":
      return leftVal > rightVal;
    case "<":
      return leftVal < rightVal;
    case ">=":
      return leftVal >= rightVal;
    case "<=":
      return leftVal <= rightVal;
    case "!=":
      return leftVal !== rightVal;
    case "=":
    case "==":
      return leftVal === rightVal;
    default:
      return false;
  }
}

export function evaluateAllRules(rules, context) {
  if (!rules || rules.length === 0) return false;
  return rules.some((rule) => evaluateSingleRule(rule, context));
}

export function gatherExcludedDatesForRow(
  rules,
  rowData,
  plantingDateStr,
  harvestDateStr,
) {
  const excluded = [];
  const GRAPH_UI_DATE_FORMAT = "DD MMM YY";
  if (!rules || rules.length === 0) return excluded;

  const plantingDate = plantingDateStr
    ? dayjs(plantingDateStr, BACKEND_DATE_FORMAT)
    : null;
  const harvestDate = harvestDateStr
    ? dayjs(harvestDateStr, BACKEND_DATE_FORMAT)
    : null;

  rowData.forEach((entry) => {
    const measureDate = dayjs(entry.date, GRAPH_UI_DATE_FORMAT);
    const contextObj = {
      plantingDate,
      harvestDate,
      dataCollectedDate: measureDate,
      averagePercentile80_95: entry.averagePercentile80_95,
      averageLength: entry.averageLength,
    };

    const shouldExclude = evaluateAllRules(rules, contextObj);
    if (shouldExclude) {
      excluded.push(entry);
    }
  });

  return excluded;
}

export const getAverageFromPercentile = (
  lengthList,
  minPercentile,
  maxPercentile,
) => {
  // Sort the leaf length list in ascending order
  const sortedList = [...lengthList].sort((a, b) => a - b);

  // Calculate the indices for the given percentiles
  const minIndex = Math.floor((minPercentile / 100) * sortedList.length);
  const maxIndex = Math.ceil((maxPercentile / 100) * sortedList.length);

  // Extract the slice of values within the percentile range
  const percentileSlice = sortedList.slice(minIndex, maxIndex);

  // Calculate the average of the slice
  const sum = percentileSlice.reduce((acc, value) => acc + value, 0);
  return parseFloat((sum / percentileSlice.length).toFixed(1));
};

export const getPercentile80_95Json = async (
  date,
  lengths,
  orgId,
  siteId,
  siteName,
  cropName,
  greenhouseName,
  plotName,
  siteMetadata
) => {
  const computeWithAverageData = () =>
    getAverageFromPercentile(lengths, 80, 95);

  try {
    // Demo/clone sites read another site's blobs (CLOUD_SITE_PREFIX) at that
    // site's dates (CLOUD_DATE_OFFSETS per bay); plain sites are unaffected.
    const cloudSitePrefix = blobSitePrefix(siteMetadata, { name: siteName });
    const jsonURL = `${cloudSitePrefix}/${cropName}/${blobDate(siteMetadata, greenhouseName, date)}/${greenhouseName}/${plotName}/percentile_(80, 95)_data.json`;
    const preSignedUrl = await getFilePreSignedUrl(
      orgId,
      siteId,
      cropName,
      jsonURL,
      siteMetadata?.CLOUD_STORAGE_NAME || "azure",
      true,
      true,
    );

    const jsonUrl = preSignedUrl?.data?.data?.url;
    if (!jsonUrl) return computeWithAverageData();

    const response = await fetch(jsonUrl);

    if (!response.ok) return computeWithAverageData();

    const jsonData = await response.json();

    return jsonData?.average_length_cm || computeWithAverageData();
  } catch (error) {
    return computeWithAverageData();
  }
};
